import React, { useEffect } from "react";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Combobox } from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import { useListMarketZones } from "@/lib/api/generalContract";
import { useListMinimalUsers } from "@/lib/api/users";
import { Commodities, CommodityOptions } from "@/lib/api/commodity";
import { useListCustomers } from "@/lib/api/customer";
import {
  CreateMarketZoneBreakdownSchema,
  UpdateMarketZoneBreakdownSchema,
  MarketZoneBreakdown 
} from "@/lib/api/risk/marketZoneBreakthrough";
import { useMemo } from "react";
import { formatNumberToDisplay } from "@/lib/api/common";
import { useListRiskSummaryFull } from "@/lib/api/risk/summary";
import { get_customer_commodity_top_level } from "../common";
import { getProjectedCropYearMap } from "./marketZoneBreakdownColumns";

interface MarketZoneBreakdownFormData {
  customer_id: string;
  market_zone_id: string;
  commodity: string;
  crop_year: number;
  month_one?: string;
  month_two?: string;
  month_three?: string;
  month_four?: string;
  month_five?: string;
  month_six?: string;
  month_seven?: string;
  month_eight?: string;
  month_nine?: string;
  month_ten?: string;
  month_eleven?: string;
  month_twelve?: string;
  total: string;
  amount: string;
  note?: string;
}

interface MarketZoneBreakdownFormProps {
  mode: "create" | "edit";
  onSubmit: (data: MarketZoneBreakdownFormData) => void;
  onCancel?: () => void;
  showCancelButton?: boolean;
  isSubmitting?: boolean;
  initialData?: Partial<MarketZoneBreakdownFormData>;
  baseTopLevelResult?: ReturnType<typeof useListRiskSummaryFull>
}

export function MarketZoneBreakdownForm({
  mode,
  onSubmit,
  onCancel,
  showCancelButton = true,
  isSubmitting = false,
  initialData,
  baseTopLevelResult,
}: MarketZoneBreakdownFormProps) {
  const marketZonesQuery = useListMarketZones();
  const minimalCustomersListResult = useListCustomers({ minimal: true, hasRiskProfile: true });

  // Use appropriate schema based on mode
  const formSchema = mode === "create" ? CreateMarketZoneBreakdownSchema : UpdateMarketZoneBreakdownSchema;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm<MarketZoneBreakdownFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      customer_id: "",
      market_zone_id: "",
      commodity: "",
      crop_year: new Date().getFullYear(),
      month_one: "",
      month_two: "",
      month_three: "",
      month_four: "",
      month_five: "",
      month_six: "",
      month_seven: "",
      month_eight: "",
      month_nine: "",
      month_ten: "",
      month_eleven: "",
      month_twelve: "",
      total: "",
      amount: "0",
      note: "",
      ...initialData,
    },
  });

  // Load existing data for edit mode
  useEffect(() => {
    if (mode === "edit" && initialData) {
      reset({
        amount: "0",
        customer_id: initialData.customer_id,
        market_zone_id: initialData.market_zone_id,
        commodity: initialData.commodity,
        crop_year: initialData.crop_year,
        month_one: initialData.month_one || "",
        month_two: initialData.month_two || "",
        month_three: initialData.month_three || "",
        month_four: initialData.month_four || "",
        month_five: initialData.month_five || "",
        month_six: initialData.month_six || "",
        month_seven: initialData.month_seven || "",
        month_eight: initialData.month_eight || "",
        month_nine: initialData.month_nine || "",
        month_ten: initialData.month_ten || "",
        month_eleven: initialData.month_eleven || "",
        month_twelve: initialData.month_twelve || "",
        total: initialData.total,
        note: initialData.note || "",
      });
    }
  }, [mode, initialData, reset]);

  // Prepare options for comboboxes
  const customerOptions = useMemo(() => {
    if (!minimalCustomersListResult.data?.data) return [];
    return minimalCustomersListResult.data.data.map((customer: { id: string; long_name: string; }) => ({
      value: customer.id,
      label: customer.long_name,
    }));
  }, [minimalCustomersListResult.data]);

  const marketZoneOptions = useMemo(() => {
    if (!marketZonesQuery.data?.data) return [];
    return marketZonesQuery.data.data
      .map((zone: { id: string; dtn_market_zone: string; }) => ({
        value: zone.id,
        label: zone.dtn_market_zone,
      }));
  }, [marketZonesQuery.data]);

  const commodityOptions = useMemo(() => {
    return CommodityOptions.map(option => ({
      value: option.value,
      label: option.label,
    }));
  }, []);

  const watchedValues = watch();

  const customer_id = watchedValues.customer_id;
  const selectedCommodity = watchedValues.commodity;
  const summaryData = baseTopLevelResult?.data?.data?.summary || {};

  // Auto-calculate total as sum of all 12 months
  useEffect(() => {
    const monthValues = [
      watchedValues.month_one,
      watchedValues.month_two,
      watchedValues.month_three,
      watchedValues.month_four,
      watchedValues.month_five,
      watchedValues.month_six,
      watchedValues.month_seven,
      watchedValues.month_eight,
      watchedValues.month_nine,
      watchedValues.month_ten,
      watchedValues.month_eleven,
      watchedValues.month_twelve,
    ];

    const total = monthValues.reduce((sum, value) => {
      const numValue = value ? parseFloat(value) : 0;
      return sum + (isNaN(numValue) ? 0 : numValue);
    }, 0);

    setValue("total", total.toString(), { shouldValidate: true });
  }, [
    watchedValues.month_one,
    watchedValues.month_two,
    watchedValues.month_three,
    watchedValues.month_four,
    watchedValues.month_five,
    watchedValues.month_six,
    watchedValues.month_seven,
    watchedValues.month_eight,
    watchedValues.month_nine,
    watchedValues.month_ten,
    watchedValues.month_eleven,
    watchedValues.month_twelve,
    setValue,
  ]);

  const { isReady: isSummaryReady, commodity_top_level_items } = useMemo(() => {
    if (!customer_id) return { isReady: false, customerName: "Loading", commodity_top_level_items: [] };
    if (!summaryData?.commodity_list) return { isReady: false, customerName: "Loading", commodity_top_level_items: [] };
    if (!summaryData?.customer_name_map) return { isReady: false, customerName: "Loading", commodity_top_level_items: [] };

    const customerName = customer_id ? summaryData.customer_name_map[customer_id] || "Unknown Customer" : "";

    const commodity_top_level_items = summaryData.commodity_list.map((commodity_type: Commodities) => get_customer_commodity_top_level({ commodity_type, customer_id, summaryData })).filter(Boolean).sort((a: any, b: any) => a.commodity_display_name.localeCompare(b.commodity_display_name));

    return { isReady: true, customerName, commodity_top_level_items };
  }, [summaryData]);

  const unsoldAmount = !selectedCommodity ? undefined : commodity_top_level_items.find((item: any) => item.commodity_type === selectedCommodity && item.physicalBreakdown.find((item: any) => item.id === 'unprotected_physical'))?.physicalBreakdown.find((item: any) => item.id === 'unprotected_physical') ?? undefined;

  const { from } =  (watchedValues.commodity) ? getProjectedCropYearMap(selectedCommodity as Commodities) : {};
  const getMonthLabel = (index: number) => {
    return moment.tz(from, CommonDestinationTimezone).add(index, "months").format("MMM");
  }
  const monthFields = [
    { key: "month_one", label: getMonthLabel(0) },
    { key: "month_two", label: getMonthLabel(1) },
    { key: "month_three", label: getMonthLabel(2) },
    { key: "month_four", label: getMonthLabel(3) },
    { key: "month_five", label: getMonthLabel(4) },
    { key: "month_six", label: getMonthLabel(5) },
    { key: "month_seven", label: getMonthLabel(6) },
    { key: "month_eight", label: getMonthLabel(7) },
    { key: "month_nine", label: getMonthLabel(8) },
    { key: "month_ten", label: getMonthLabel(9) },
    { key: "month_eleven", label: getMonthLabel(10) },
    { key: "month_twelve", label: getMonthLabel(11) },
  ] as const;

  const isFieldDisabled = (fieldName: string) => {
    if (mode === "create") return false;
    // In edit mode, disable customer, market zone, commodity, and crop year
    return ["customer_id", "market_zone_id", "commodity", "crop_year"].includes(fieldName);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Customer */}
        <div className="grid">
          <Label htmlFor="customer_id">Customer *</Label>
          <Combobox
            options={customerOptions}
            value={watchedValues.customer_id}
            onValueChange={(value) => setValue("customer_id", value)}
            placeholder="Select customer"
            dwimOnChange
            isModal={true}
            disabled={isFieldDisabled("customer_id") || (mode === "create" && !!initialData?.customer_id)}
          />
          {errors.customer_id && (
            <p className="text-sm text-red-600 mt-1">{errors.customer_id.message}</p>
          )}
        </div>

        {/* Market Zone */}
        <div className="grid">
          <Label htmlFor="market_zone_id">Market Zone *</Label>
          <Combobox
            options={marketZoneOptions}
            value={watchedValues.market_zone_id}
            onValueChange={(value) => setValue("market_zone_id", value)}
            placeholder="Select market zone"
            dwimOnChange
            isModal={true}
            disabled={isFieldDisabled("market_zone_id")}
          />
          {errors.market_zone_id && (
            <p className="text-sm text-red-600 mt-1">{errors.market_zone_id.message}</p>
          )}
        </div>

        {/* Market Zone */}
        <div className="grid">
          <Label htmlFor="commodity_id">Commodity *</Label>
          <Combobox
            options={commodityOptions}
            value={watchedValues.commodity}
            onValueChange={(value) => setValue("commodity", value)}
            placeholder="Select commodity"
            dwimOnChange
            isModal={true}
            disabled={isFieldDisabled("commodity")}
          />
          {errors.commodity && (
            <p className="text-sm text-red-600 mt-1">{errors.commodity.message}</p>
          )}
        </div>

        {/* Crop Year */}
        <div className="grid">
          <Label htmlFor="crop_year">Crop Year *</Label>
          <Input
            id="crop_year"
            type="number"
            {...register("crop_year", { valueAsNumber: true })}
            disabled={isFieldDisabled("crop_year")}
          />
          {errors.crop_year && (
            <p className="text-sm text-red-600 mt-1">{errors.crop_year.message}</p>
          )}
        </div>

        {/* Total */}
        <div className="grid">
          <Label htmlFor="total">Total *</Label>
          <Input
            disabled
            id="total"
            {...register("total")}
            placeholder="Enter total amount"
          />
          {errors.total && (
            <p className="text-sm text-red-600 mt-1">{errors.total.message}</p>
          )}
        </div>

        {unsoldAmount && (
          <div className="flex flex-col md:grid gap-2">
            <p  className="text-sm font-bold">Unsold Amount</p>
            <span className="text-xs">{formatNumberToDisplay(unsoldAmount?.value, { emptyFormat: '--', zeroFormat: '--' })} ({formatNumberToDisplay(unsoldAmount?.percent, { emptyFormat: '--', zeroFormat: '--', suffix: "%" })})</span>
          </div>
        )}
      </div>

      {/* Monthly Breakdown */}
      <div>
        <Label className="text-base font-semibold">Monthly Breakdown</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-2">
          {monthFields.map(({ key, label }) => (
            <div key={key}>
              <Label htmlFor={key}>{label}</Label>
              <Input
                id={key}
                {...register(key)}
                placeholder="0"
              />
              {errors[key] && (
                <p className="text-sm text-red-600 mt-1">{errors[key]?.message}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Note */}
      <div>
        <Label htmlFor="note">Note</Label>
        <Textarea
          id="note"
          {...register("note")}
          placeholder="Optional note"
          rows={3}
        />
        {errors.note && (
          <p className="text-sm text-red-600 mt-1">{errors.note.message}</p>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
        {showCancelButton && onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
            className="sm:order-1"
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={isSubmitting}
          className="sm:order-2"
        >
          {isSubmitting
            ? mode === "create"
              ? "Creating..."
              : "Updating..."
            : mode === "create"
            ? "Create Breakdown"
            : "Update Breakdown"}
        </Button>
      </div>
    </form>
  );
}
